import { processingFailedReason, processingReason, uploadCancelledReason } from '@lib/verbs';
import { FileUploadStatus } from '@stores/UploadManager/UploadManager';
import { Media, MediaStatus } from '@typings/Media';

/**
 * Shared utilities for media status determination, labels, and tooltips.
 * Used by both grid view (MediaCard) and table view (StatusCell).
 */

// ============================================================================
// Status Type Determination
// ============================================================================

/**
 * Extended status type that includes upload-specific states and processing subtypes.
 */
export type MediaDisplayStatus =
  | 'queued-upload' // Queued for upload (from FileUploadStatus)
  | 'uploading' // Actively uploading
  | 'queued' // Backend queued state
  | 'processing-with-video' // Processing, video preview available
  | 'processing-without-video' // Processing, no video preview yet
  | 'errored'
  | 'completed'
  | 'empty'
  | 'cancelled';

/**
 * Check if media is in a "retrieving" phase (queued or uploading from backend perspective).
 */
export function isRetrieving(media: Media): boolean {
  return media.status === 'queued' || media.status === 'uploading';
}

/**
 * Every status that is still on its way to `completed`.
 *
 * Declared `satisfies` the status union so removing or renaming a member breaks here rather
 * than silently reclassifying it. A bare `string[]` would compile clean and flip the meaning.
 */
const STILL_PROCESSING_STATUSES = [
  'empty',
  'uploading',
  'queued',
  'processing',
] as const satisfies readonly MediaStatus[];

/**
 * Whether this media is still on its way, as opposed to finished or stopped.
 *
 * The distinction that matters to callers is NOT `!isCompleted`: that also covers `errored` and
 * `cancelled`, which will never progress. Anything that promises a user "this will be available
 * once processing finishes" must ask THIS question — an errored item satisfies `!isCompleted`
 * and would be told to wait for something that is never coming.
 */
export function isStillProcessing(media: Pick<Media, 'status'>): boolean {
  return (STILL_PROCESSING_STATUSES as readonly MediaStatus[]).includes(media.status);
}

/**
 * Why a verb is unavailable on this media — or `undefined` when it isn't.
 *
 * The one place that turns a status into an explanation, so every surface that dims a verb says
 * the same thing for the same reason. Callers pass the verb's own label because the sentence
 * leads with it; `MomentModal`'s clip-scoped agent verb isn't in the catalog and still needs
 * this, which is why the parameter is a string rather than a `VerbId`.
 *
 * Total over `MediaStatus` by construction: `completed` is the only status with nothing to
 * explain, `isStillProcessing` claims four more, `errored` is named, and `cancelled` is what's
 * left — so the last branch needs no condition and no default. Adding a status to the union
 * without deciding its wording breaks `STILL_PROCESSING_STATUSES` above rather than silently
 * landing in the cancelled branch.
 *
 * Lives here rather than in `@lib/verbs` because this is a status question; the catalog owns the
 * wording and knows nothing about `MediaStatus`.
 */
export function mediaVerbUnavailableReason(
  label: string,
  media: Pick<Media, 'status'>,
): string | undefined {
  if (media.status === 'completed') return undefined;
  if (isStillProcessing(media)) return processingReason(label);
  if (media.status === 'errored') return processingFailedReason(label);
  return uploadCancelledReason(label);
}

/**
 * Check if media is processing with video preview available.
 */
export function isProcessingWithVideo(media: Media): boolean {
  return media.status === 'processing' && media.capabilities?.preview === true;
}

/**
 * Check if media is processing without video preview.
 */
export function isProcessingWithoutVideo(media: Media): boolean {
  return media.status === 'processing' && !media.capabilities?.preview;
}

/**
 * Check if media is completed.
 */
export function isCompleted(media: Media): boolean {
  return media.status === 'completed';
}

/**
 * Check if the media modal can be opened for this media.
 * Modal can be opened when media is queued, processing (with or without video), or completed.
 */
export function canOpenMediaModal(media: Media): boolean {
  return (
    media.status === 'queued' ||
    isCompleted(media) ||
    isProcessingWithVideo(media) ||
    isProcessingWithoutVideo(media)
  );
}

/**
 * A client-side upload that failed/cancelled while its backend media is still
 * pre-processing ('empty'/'uploading') — i.e. an orphaned failed upload the
 * backend still reports as uploading. Shared so the grid (MediaCard) and table
 * (getMediaDisplayStatus) classify it identically. Each surface keeps its own
 * presentation (the grid shows a bespoke remove-only overlay).
 */
export function isFailedOrphanUpload(
  media: Pick<Media, 'status'>,
  uploadStatus: FileUploadStatus | null | undefined,
): boolean {
  const uploadFailed = uploadStatus?.status === 'error' || uploadStatus?.status === 'cancelled';
  return uploadFailed && (media.status === 'empty' || media.status === 'uploading');
}

/**
 * Statuses the **backend** advances on its own. Always worth polling: left
 * alone, they will change.
 */
const BACKEND_DRIVEN_STATUSES: readonly string[] = [
  'queued',
  'processing',
] as const satisfies readonly MediaStatus[];

/**
 * Pre-finish states. The backend never moves these by itself — they advance
 * only when the uploading client calls finish.
 */
const PRE_FINISH_STATUSES: readonly string[] = [
  'empty',
  'uploading',
] as const satisfies readonly MediaStatus[];

/** True for a status that can only advance once the client finishes its upload. */
export const isPreFinishStatus = (status: string): boolean => PRE_FINISH_STATUSES.includes(status);

/**
 * True for a status the backend will move on its own.
 *
 * Distinguished from pre-finish because the two justify very different
 * patience: a pre-finish media with no live upload is going nowhere, whereas a
 * `processing` media can legitimately sit at one progress value for many
 * minutes while a single worker stage runs.
 */
export const isBackendDrivenStatus = (status: string): boolean =>
  BACKEND_DRIVEN_STATUSES.includes(status);

/**
 * Whether a media still warrants status polling — i.e. whether its status can
 * still change on its own.
 *
 * **This is the termination condition for all media status polling**, so the
 * `hasLiveUpload` leg is load-bearing rather than a nicety. A library
 * accumulates `empty` and stale `uploading` media from abandoned uploads
 * (reclaimed later by the empty-media dedup on re-upload — see
 * upload-failure-modes.md §6). Those never advance by themselves, so counting
 * them as pollable keeps the poller running forever on a library where
 * everything has actually finished. That shipped once and was caught by
 * watching the network tab, not by types.
 *
 * @param status         Backend media status.
 * @param hasLiveUpload  Whether this client has an upload still in flight for
 *                       it (`uploadManager.hasLiveUploadForMedia(id)`). Only
 *                       consulted for pre-finish statuses, which cannot
 *                       advance without it.
 */
export function isStatusPollable(status: string, hasLiveUpload = false): boolean {
  if (BACKEND_DRIVEN_STATUSES.includes(status)) return true;
  if (PRE_FINISH_STATUSES.includes(status)) return hasLiveUpload;
  return false;
}

/**
 * Determine the display status for a media item, considering both backend status
 * and active upload status.
 */
export function getMediaDisplayStatus(
  media: Media,
  uploadStatus?: FileUploadStatus | null,
): MediaDisplayStatus {
  // Failed/cancelled upload whose backend media is still pre-processing — show the
  // failure instead of a stuck "Uploading".
  if (isFailedOrphanUpload(media, uploadStatus)) return 'errored';

  // If there's an active upload and backend hasn't transitioned yet
  const backendHasFile =
    media.status === 'queued' || media.status === 'processing' || media.status === 'completed';

  if (uploadStatus && !backendHasFile) {
    if (uploadStatus.status === 'queued') {
      return 'queued-upload';
    }
    if (uploadStatus.status === 'uploading' || uploadStatus.status === 'success') {
      return 'uploading';
    }
  }

  // Backend status takes precedence once file is being processed
  switch (media.status) {
    case 'processing':
      return media.capabilities?.preview ? 'processing-with-video' : 'processing-without-video';
    case 'queued':
    case 'uploading':
    case 'errored':
    case 'completed':
    case 'empty':
    case 'cancelled':
      return media.status;
    default:
      return 'empty';
  }
}

// ============================================================================
// Status Labels
// ============================================================================

/**
 * Get the display label for a media status.
 * @param displayStatus - The display status type
 * @param progress - Optional progress percentage (0-100)
 */
export function getStatusLabel(displayStatus: MediaDisplayStatus, progress?: number): string {
  switch (displayStatus) {
    case 'queued-upload':
      return 'Queued for upload';
    case 'uploading':
      return progress !== undefined ? `Uploading ${progress}%` : 'Uploading';
    case 'queued':
    case 'processing-with-video':
    case 'processing-without-video':
      return progress !== undefined ? `Processing (${progress}%)` : 'Processing media';
    case 'errored':
      return 'Errored';
    case 'completed':
      return 'Indexed';
    case 'empty':
      return 'Processing media'; // Show as processing during transition
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Unknown';
  }
}

// ============================================================================
// Tooltips
// ============================================================================

/**
 * Get the tooltip text for a media status.
 * @param displayStatus - The display status type
 * @param title - Optional media title for personalized messages
 */
export function getStatusTooltip(displayStatus: MediaDisplayStatus, title?: string): string {
  const titleSuffix = title ? ` (${title})` : '';

  switch (displayStatus) {
    case 'queued-upload':
      return `Queued for upload: ${title || 'Media'}`;
    case 'uploading':
      return `Uploading ${title || 'media'}`;
    case 'queued':
      return `We are processing your media${titleSuffix}. It will be available to preview soon.`;
    case 'processing-with-video':
      return `You can already watch this media${titleSuffix}. Search will be available once processing is complete.`;
    case 'processing-without-video':
      return `We are processing your media${titleSuffix}. It will be available to preview soon.`;
    case 'errored':
      return 'This file is errored and needs to be re-uploaded. Please contact support if the problem persists.';
    case 'completed':
      return `${title || 'Media'} is ready`;
    case 'empty':
      return `Processing ${title || 'media'}...`;
    case 'cancelled':
      return `Upload cancelled: ${title || 'Media'}`;
    default:
      return '';
  }
}

// ============================================================================
// Progress Calculation
// ============================================================================

/**
 * Calculate the display progress for a media item.
 * - For processing (with or without a video preview): real progress scaled to 0-100%
 * - For everything else: no known progress (undefined)
 *
 * @param media - The media object
 */
export function getDisplayProgress(media: Media): number | undefined {
  if (media.status === 'processing') {
    // `progress` is absent unless MediaStatusPoller has merged it in from
    // `stats/processing` — no media endpoint returns it. `isFinite` also
    // rules out NaN and ±Infinity.
    const { progress } = media;
    if (progress === undefined || !Number.isFinite(progress)) {
      return undefined;
    }
    return Math.floor(progress * 100);
  }

  return undefined;
}

/**
 * `getDisplayProgress`, with "no known progress" collapsed to 0.
 *
 * Only safe where the result feeds a numeric fill (a progress-bar width, a
 * batch average) rather than user-facing text — collapsing to 0 there just
 * means "contributes nothing yet". Never use this to build a percentage
 * label: `getDisplayProgress`'s `undefined` means "we don't know", and a
 * label must keep that distinction (render no percentage) rather than claim
 * a fabricated 0%.
 */
export function getDisplayProgressOrZero(media: Media): number {
  return getDisplayProgress(media) ?? 0;
}

// ============================================================================
// Alt Text
// ============================================================================

/**
 * Generate meaningful alt text for a media item.
 */
export function getMediaAltText(media: Media): string {
  return media.metadata?.name || media.metadata?.filename || `Media ${media._id}`;
}

/**
 * Get the effective duration for a media item.
 * Returns undefined when duration is not yet known (e.g. still processing).
 *
 * The backend stores duration=0 for unprocessed media, so we treat 0 as "unknown"
 * and fall through to the external source (e.g. YouTube userMetadata).
 */
export function getMediaDuration(media: Media): number | undefined {
  return media.metadata?.video?.duration || media.userMetadata?.duration || undefined;
}

/**
 * Whether a media item is most likely a still image.
 * Inferred from frameCount === 1 (single-frame "videos" the backend produces for images).
 *
 * Structurally typed so virtual upload rows and other Media-shaped types can use it.
 */
export function isProbablyImage(media: {
  metadata?: { video?: { frameCount?: number } };
}): boolean {
  return media.metadata?.video?.frameCount === 1;
}
